import React, { useState, useEffect, useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getAIGuidance } from 'services/aiGuidance';

const AIGuidance = ({
  fieldName,
  fieldLabel,
  currentContent,
  category,
  rubricData,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  const fetchGuidance = useCallback(async () => {
    if (!category || !fieldName || !fieldLabel) return;

    setLoading(true);
    try {
      let recaptchaToken;
      if (
        import.meta.env.VITE_DISABLE_RECAPTCHA !== 'true' &&
        executeRecaptcha
      ) {
        recaptchaToken = await executeRecaptcha('ai_guidance');
      }
      const result = await getAIGuidance(
        fieldName,
        fieldLabel,
        currentContent,
        category,
        rubricData,
        recaptchaToken || 'dev-bypass'
      );
      setGuidance(result);
      setCurrentTipIndex(0); // Reset to first tip when fetching new guidance
    } catch {
      setGuidance({
        guidance:
          "Consider including relevant details that showcase your work's impact and innovation.",
        error: true,
      });
      setCurrentTipIndex(0);
    } finally {
      setLoading(false);
    }
  }, [fieldName, fieldLabel, currentContent, category, rubricData]);

  useEffect(() => {
    if (showGuidance && !guidance && !loading) {
      fetchGuidance();
    }
  }, [
    showGuidance,
    guidance,
    loading,
    fieldName,
    fieldLabel,
    currentContent,
    category,
    rubricData,
    fetchGuidance,
  ]);

  const cycleToNextTip = () => {
    if (!guidance || guidance.error) return;

    const tips = guidance.guidance.split('\n').filter(line => line.trim());
    if (tips.length === 0) return;

    setCurrentTipIndex(prevIndex => (prevIndex + 1) % tips.length);
  };

  if (!category) return null;

  return (
    <div className="relative">
      {/* AI Guidance Toggle Button */}
      <button
        onClick={() => setShowGuidance(!showGuidance)}
        className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-800 transition-colors font-medium bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-md"
        type="button"
      >
        <svg
          className="w-3 h-3 text-yellow-500 drop-shadow-sm animate-pulse"
          fill="currentColor"
          stroke="none"
          viewBox="0 0 24 24"
          style={{
            filter: 'drop-shadow(0 0 2px rgba(255, 193, 7, 0.4))',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
        Tips
        <svg
          className={`w-2.5 h-2.5 transition-transform ${showGuidance ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* AI Guidance Panel */}
      {showGuidance && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg p-3 shadow-lg z-10 w-64">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-600">
              <svg
                className="animate-spin w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-xs">Loading...</span>
            </div>
          ) : guidance ? (
            <div>
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-xs font-semibold text-gray-900">
                  Quick Tips
                </h4>
                <button
                  onClick={() => setShowGuidance(false)}
                  className="text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="text-xs text-gray-700 leading-relaxed">
                {guidance.error ? (
                  <p className="text-gray-600 italic">{guidance.guidance}</p>
                ) : (
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-1 mt-0.5">•</span>
                    <span>
                      {guidance.guidance
                        .split('\n')
                        .filter(line => line.trim())[currentTipIndex] ||
                        guidance.guidance
                          .split('\n')
                          .filter(line => line.trim())[0]}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center">
                <button
                  onClick={cycleToNextTip}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                  type="button"
                >
                  Next tip
                </button>
                <button
                  onClick={fetchGuidance}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                  type="button"
                >
                  New tips
                </button>
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-600">
              Click to get helpful tips for this field.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIGuidance;
